import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Hash } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const KeywordDensityCalculator = () => {
  const [content, setContent] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");

  const result = useMemo(() => {
    if (!content || !keyword) return null;
    
    const words = content.toLowerCase().split(/\s+/).filter(w => w.length > 0);
    const totalWords = words.length;
    
    if (totalWords === 0) return null;
    
    const keywordLower = keyword.toLowerCase().trim();
    const keywordWords = keywordLower.split(/\s+/);
    
    let count = 0;
    if (keywordWords.length === 1) {
      count = words.filter(w => w.includes(keywordLower)).length;
    } else {
      const regex = new RegExp(keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      const matches = content.match(regex);
      count = matches ? matches.length : 0;
    }
    
    const density = (count / totalWords) * 100;
    
    return { count, totalWords, density };
  }, [content, keyword]);

  const reset = () => {
    setContent("");
    setKeyword("");
  };

  const getAssessment = (density: number) => {
    if (density >= 0.5 && density <= 2.5) return { label: "Optimal", color: "text-green-600" };
    if (density >= 0.3 && density < 0.5) return { label: "Low", color: "text-yellow-600" };
    if (density > 2.5 && density <= 3.5) return { label: "High", color: "text-yellow-600" };
    if (density < 0.3) return { label: "Too Low", color: "text-orange-500" };
    return { label: "Over-optimized", color: "text-red-600" };
  };

  const relatedTools = getRelatedTools("keyword-density");

  return (
    <ToolPageLayout
      title="Keyword Density Calculator"
      description="Analyze keyword density in your content to optimize for SEO without over-optimization."
      metaDescription="Keyword density measures how often a keyword appears relative to total word count. Proper keyword usage helps search engines understand your content while avoiding penalties for keyword stuffing."
      icon={Hash}
      slug="keyword-density"
      formula={{
        equation: "Keyword Density = (Keyword Occurrences / Total Words) × 100",
        variables: [
          { name: "Keyword Occurrences", description: "Number of times the keyword appears in the text" },
          { name: "Total Words", description: "Total word count of the content" },
        ]
      }}
      example={{
        inputs: [
          { label: "Content", value: "500 words article" },
          { label: "Keyword", value: "digital marketing" },
          { label: "Occurrences", value: "8 times" },
        ],
        output: "1.6% Density",
        interpretation: "1.6% is within the optimal range (0.5-2.5%). Your content is well-optimized without being spammy."
      }}
      improvements={[
        "Aim for 0.5-2.5% keyword density for primary keywords",
        "Use keyword variations and synonyms naturally",
        "Place keywords in headings, intro, and conclusion",
        "Focus on reader experience over keyword count",
        "Use LSI keywords to add semantic context",
        "Avoid forced or unnatural keyword placement",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "SEO Services", href: "/seo" }}
    >
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Analyze Content</CardTitle>
            <CardDescription>Paste your content and enter keyword</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="keyword">Target Keyword</Label>
              <Input
                id="keyword"
                placeholder="digital marketing"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                placeholder="Paste your content here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[200px]"
              />
            </div>

            <Button variant="outline" onClick={reset} className="w-full">
              Reset
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Analysis Results</CardTitle>
            <CardDescription>Keyword density breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-6">
                <div className="text-center p-6 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-sm text-muted-foreground mb-1">Keyword Density</p>
                  <p className={`text-4xl font-bold ${getAssessment(result.density).color}`}>
                    {result.density.toFixed(2)}%
                  </p>
                  <p className={`text-sm mt-2 ${getAssessment(result.density).color}`}>
                    {getAssessment(result.density).label}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-semibold text-foreground">{result.count}</p>
                    <p className="text-xs text-muted-foreground">Keyword Count</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-semibold text-foreground">{result.totalWords}</p>
                    <p className="text-xs text-muted-foreground">Total Words</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-8">
                Enter a keyword and content to analyze density.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </ToolPageLayout>
  );
};

export default KeywordDensityCalculator;
